import "@dxos/node-std/globals"

// packages/common/random-access-storage/src/common/abstract-storage.ts
import { join as join2 } from "@dxos/node-std/path";
import { inspect } from "@dxos/node-std/util";
import { inspectObject } from "@dxos/debug";
import { log as log2 } from "@dxos/log";

// packages/common/random-access-storage/src/common/utils.ts
import { join } from "@dxos/node-std/path";
var stringDiff = (first, second) => first.split(second).join("");
var getFullPath = (root, path) => join(root, stringDiff(path, root));

// packages/common/random-access-storage/src/common/directory.ts
var Directory = class {
  constructor(type, path, _getFiles, _getOrCreateFile, _delete) {
    this.type = type;
    this.path = path;
    this._getFiles = _getFiles;
    this._getOrCreateFile = _getOrCreateFile;
    this._delete = _delete;
  }
  toString() {
    return `Directory(${JSON.stringify({
      type: this.type,
      path: this.path
    })})`;
  }
  /**
  * Create a new sub-directory.
  */
  createDirectory(path) {
    return new Directory(this.type, getFullPath(this.path, path), this._getFiles, this._getOrCreateFile, this._delete);
  }
  /**
  * Get all files in the current directory.
  */
  getFiles() {
    return this._getFiles();
  }
  /**
  * Get or create a new file.
  */
  getOrCreateFile(filename, opts) {
    return this._getOrCreateFile(this.path, filename, opts);
  }
  /**
  * Close and delete all files in the directory and all its sub-directories.
  */
  async delete() {
    await this._delete();
  }
};

// packages/common/random-access-storage/src/common/file.ts
import pify from "pify";
import { log } from "@dxos/log";
var pifyFields = (object, type, fields) => {
  for (const field of fields) {
    if (!object[field]) {
    } else {
      const fn = pify(object[field].bind(object));
      object[field] = async (...args) => {
        const before = performance.now();
        const res = await fn(...args);
        const elapsed = performance.now() - before;
        if (elapsed > 10) {
          log.warn("Slow storage operation", {
            type,
            operation: field,
            elapsed
          }, {
            file: "file.ts",
            line: 55,
            scope: void 0,
            callSite: (f, a) => f(...a)
          });
        }
        return res;
      };
    }
  }
  return object;
};
var wrapFile = (native, type) => {
  const file = pifyFields(native, type, [
    "write",
    "read",
    "del",
    "stat",
    "close",
    "destroy",
    "truncate"
  ]);
  return Object.assign(file, {
    type,
    native
  });
};

// packages/common/random-access-storage/src/common/abstract-storage.ts
var AbstractStorage = class {
  // TODO(burdon): Make required.
  constructor(path) {
    this.path = path;
    this._files = /* @__PURE__ */ new Map();
  }
  [inspect.custom]() {
    return inspectObject(this);
  }
  toJSON() {
    return {
      type: this.type,
      path: this.path
    };
  }
  get size() {
    return this._files.size;
  }
  // TODO(burdon): Make required.
  createDirectory(sub = "") {
    return new Directory(this.type, getFullPath(this.path, sub), () => this._getFiles(sub), (...args) => this.getOrCreateFile(...args), () => this._delete(sub));
  }
  /**
  * Delete all files.
  */
  async reset() {
    try {
      log2.warn("Erasing all data...", {}, {
        file: "abstract-storage.ts",
        line: 59,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      await this._closeFilesInPath("");
      await this._delete("");
      await this._destroy();
      log2("Erased...", {}, {
        file: "abstract-storage.ts",
        line: 63,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    } catch (err) {
      log2.catch(err, {}, {
        file: "abstract-storage.ts",
        line: 65,
        scope: this,
        callSite: (f, a) => f(...a)
      });
    }
  }
  getOrCreateFile(path, filename, opts) {
    const fullPath = join2(path, filename);
    let native;
    let file = this._getFileIfExists(fullPath);
    if (file) {
      if (!file.closed) {
        return file;
      }
      native = this._openFile(file.native);
    }
    if (!native) {
      native = this._createFile(path, filename, opts);
    }
    file = wrapFile(native, this.type);
    this._files.set(fullPath, file);
    return file;
  }
  _destroy() {
    return void 0;
  }
  /**
  * Attempt to reopen file.
  */
  _openFile(file) {
    return void 0;
  }
  _getFileIfExists(filename) {
    if (this._files.has(filename)) {
      const file = this._files.get(filename);
      if (file && !file.destroyed) {
        return file;
      }
    }
  }
  _getFiles(path) {
    const fullPath = getFullPath(this.path, path);
    return new Map([
      ...this._files.entries()
    ].filter(([path2, file]) => path2.includes(fullPath) && file.destroyed !== true));
  }
  async _closeFilesInPath(path) {
    await Promise.all(Array.from(this._getFiles(path).values()).map((file) => file.close().catch((err) => log2.catch(err, {}, {
      file: "abstract-storage.ts",
      line: 122,
      scope: this,
      callSite: (f, a) => f(...a)
    }))));
  }
  // TODO(burdon): Delete directory (not just listed files).
  async _delete(path) {
    await Promise.all(Array.from(this._getFiles(path)).map(([path2, file]) => {
      return file.destroy().then(() => this._files.delete(path2)).catch((err) => log2.error(err.message, {}, {
        file: "abstract-storage.ts",
        line: 133,
        scope: this,
        callSite: (f, a) => f(...a)
      }));
    }));
  }
};

// packages/common/random-access-storage/src/common/memory-storage.ts
import ram from "random-access-memory";

// packages/common/random-access-storage/src/common/storage.ts
var StorageType;
(function(StorageType2) {
  StorageType2["RAM"] = "ram";
  StorageType2["IDB"] = "idb";
  StorageType2["CHROME"] = "chrome";
  StorageType2["FIREFOX"] = "firefox";
  StorageType2["NODE"] = "node";
  StorageType2["WEBFS"] = "webfs";
})(StorageType || (StorageType = {}));

// packages/common/random-access-storage/src/common/memory-storage.ts
var MemoryStorage = class extends AbstractStorage {
  constructor() {
    super(...arguments);
    this.type = StorageType.RAM;
  }
  _createFile(path, filename) {
    return this._patchFile(ram());
  }
  _openFile(file) {
    const newFile = file.clone();
    newFile.closed = false;
    return this._patchFile(newFile);
  }
  _patchFile(file) {
    const trueRead = file.read.bind(file);
    file.read = (offset, size, cb) => trueRead(offset, size, (err, data) => {
      if (err) {
        return cb(err);
      } else {
        return cb(err, Buffer.from(data));
      }
    });
    return file;
  }
};

// packages/common/random-access-storage/src/browser/firefox-storage.ts
import raw from "random-access-web/mutable-file-wrapper.js";

// packages/common/random-access-storage/src/browser/browser-storage.ts
import { join as join3 } from "@dxos/node-std/path";
var BrowserStorage = class extends AbstractStorage {
  constructor(path) {
    super(path);
    this._fileStorage = this._createFileStorage(path);
  }
  _createFile(path, filename) {
    const fullPath = join3(path, filename);
    return this._fileStorage(fullPath);
  }
  async _destroy() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(this.path);
      request.onsuccess = () => {
        resolve();
      };
      request.onupgradeneeded = () => {
        reject(new Error("Upgrade needed."));
      };
      request.onblocked = () => {
        reject(new Error("Blocked."));
      };
      request.onerror = (err) => {
        reject(err);
      };
    });
  }
};

// packages/common/random-access-storage/src/browser/firefox-storage.ts
var FirefoxStorage = class extends BrowserStorage {
  constructor() {
    super(...arguments);
    this.type = StorageType.FIREFOX;
  }
  _createFileStorage(path) {
    return raw({
      name: path
    });
  }
};

// packages/common/random-access-storage/src/browser/idb-storage.ts
import idb from "random-access-idb";
var IDbStorage = class extends BrowserStorage {
  constructor() {
    super(...arguments);
    this.type = StorageType.IDB;
  }
  _createFileStorage(path) {
    return idb(path);
  }
};

// packages/common/random-access-storage/src/browser/web-fs.ts
import assert from "@dxos/node-std/assert";
import { EventEmitter } from "@dxos/node-std/events";
import { callbackify } from "@dxos/node-std/util";
import { synchronized } from "@dxos/async";
import { log as log3 } from "@dxos/log";
var __decorate = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var WebFS = class {
  constructor(path) {
    this.path = path;
    this.type = StorageType.WEBFS;
    this._files = /* @__PURE__ */ new Map();
  }
  get size() {
    return this._files.size;
  }
  _getFiles(path) {
    const fullName = this._getFullFilename(this.path, path);
    return new Map([
      ...this._files.entries()
    ].filter(([path2, file]) => path2.includes(fullName) && file.destroyed !== true));
  }
  async _initialize() {
    if (this._root) {
      return this._root;
    }
    this._root = await navigator.storage.getDirectory();
    assert(this._root, "Root is undefined");
    return this._root;
  }
  createDirectory(sub = "") {
    return new Directory(this.type, getFullPath(this.path, sub), () => this._getFiles(sub), (...args) => this.getOrCreateFile(...args), () => this._delete(sub));
  }
  getOrCreateFile(path, filename, opts) {
    const fullName = this._getFullFilename(path, filename);
    const existingFile = this._files.get(fullName);
    if (existingFile) {
      return existingFile;
    }
    const file = this._createFile(fullName);
    this._files.set(fullName, file);
    return file;
  }
  _createFile(fullName) {
    return new WebFile({
      file: this._initialize().then((root) => root.getFileHandle(fullName, {
        create: true
      })),
      destroy: async () => {
        this._files.delete(fullName);
        const root = await this._initialize();
        return root.removeEntry(fullName);
      }
    });
  }
  async _delete(path) {
    await Promise.all(Array.from(this._getFiles(path)).map(async ([path2, file]) => {
      await file.destroy().catch((err) => log3.catch(err, {}, {
        file: "web-fs.ts",
        line: 82,
        scope: this,
        callSite: (f, a) => f(...a)
      }));
      this._files.delete(path2);
    }));
  }
  async reset() {
    await this._delete("");
    this._root = void 0;
  }
  _getFullFilename(path, filename) {
    if (filename) {
      return getFullPath(path, filename).split("/").join("_");
    } else {
      return path.split("/").join("_");
    }
  }
};
__decorate([
  synchronized
], WebFS.prototype, "_initialize", null);
var _a;
var WebFile = class extends EventEmitter {
  constructor({ file, destroy }) {
    super();
    this.opened = true;
    this.suspended = false;
    this.closed = false;
    this.unlinked = false;
    this.writing = false;
    this.readable = true;
    this.writable = true;
    this.deletable = true;
    this.truncatable = true;
    this.statable = true;
    this.destroyed = false;
    this.directory = "";
    this.filename = "";
    this.type = StorageType.WEBFS;
    this.native = {
      write: callbackify(this.write.bind(this)),
      read: callbackify(this.read.bind(this)),
      del: callbackify(this.del.bind(this)),
      stat: callbackify(this.stat.bind(this)),
      destroy: callbackify(this.destroy.bind(this)),
      truncate: callbackify((_a = this.truncate) == null ? void 0 : _a.bind(this))
    };
    this._fileHandle = file;
    this._destroy = destroy;
  }
  async write(offset, data) {
    const fileHandle = await this._fileHandle;
    const writable = await fileHandle.createWritable({
      keepExistingData: true
    });
    await writable.write({
      type: "write",
      data,
      position: offset
    });
    await writable.close();
  }
  async read(offset, size) {
    const fileHandle = await this._fileHandle;
    const file = await fileHandle.getFile();
    if (offset + size > file.size) {
      throw new Error("Read out of bounds");
    }
    return Buffer.from(new Uint8Array(await file.slice(offset, offset + size).arrayBuffer()));
  }
  async del(offset, size) {
    if (offset < 0 || size < 0) {
      return;
    }
    const fileHandle = await this._fileHandle;
    const writable = await fileHandle.createWritable({
      keepExistingData: true
    });
    const file = await fileHandle.getFile();
    let leftoverSize = 0;
    if (offset + size < file.size) {
      const leftover = Buffer.from(new Uint8Array(await file.slice(offset + size, file.size).arrayBuffer()));
      leftoverSize = leftover.length;
      await writable.write({
        type: "write",
        data: leftover,
        position: offset
      });
    }
    await writable.write({
      type: "truncate",
      size: offset + leftoverSize
    });
    await writable.close();
  }
  async stat() {
    const fileHandle = await this._fileHandle;
    const file = await fileHandle.getFile();
    return {
      size: file.size
    };
  }
  async close() {
  }
  async destroy() {
    this.destroyed = true;
    return await this._destroy();
  }
  async truncate(offset) {
    const fileHandle = await this._fileHandle;
    const writable = await fileHandle.createWritable({
      keepExistingData: true
    });
    await writable.write({
      type: "truncate",
      size: offset
    });
    await writable.close();
  }
};
__decorate([
  synchronized
], WebFile.prototype, "write", null);
__decorate([
  synchronized
], WebFile.prototype, "read", null);
__decorate([
  synchronized
], WebFile.prototype, "del", null);
__decorate([
  synchronized
], WebFile.prototype, "stat", null);
__decorate([
  synchronized
], WebFile.prototype, "destroy", null);
__decorate([
  synchronized
], WebFile.prototype, "truncate", null);

// packages/common/random-access-storage/src/browser/storage.ts
var createStorage = ({ type, root = "" } = {}) => {
  let insideWebkit;
  try {
    insideWebkit = mochaExecutor.environment === "webkit";
  } catch (e) {
    insideWebkit = false;
  }
  if (type === void 0) {
    if (navigator && navigator.storage && typeof navigator.storage.getDirectory === "function" && !insideWebkit) {
      return new WebFS(root);
    } else {
      return new IDbStorage(root);
    }
  }
  switch (type) {
    case StorageType.RAM: {
      return new MemoryStorage(root);
    }
    case StorageType.IDB:
    case StorageType.CHROME: {
      return new IDbStorage(root);
    }
    case StorageType.FIREFOX: {
      return new FirefoxStorage(root);
    }
    case StorageType.WEBFS: {
      return new WebFS(root);
    }
    default: {
      throw new Error(`Invalid type: ${type}`);
    }
  }
};
export {
  AbstractStorage,
  Directory,
  MemoryStorage,
  StorageType,
  createStorage,
  getFullPath,
  stringDiff,
  wrapFile
};
//# sourceMappingURL=index.mjs.map
